import React, { useState } from 'react';
import { toast } from 'sonner';
import Widget from './Widget';
import OpenPositions from '../OpenPositions';
import { useAccountData } from '../../context/AccountDataProvider';
import { useTrade } from '../../context/TradeProvider';
import { closeTrade as closeTradeApi, closeOrphanPosition } from '../../api/api';
import {
  closeOpenPosition,
  closeOpenPositionsSequentially,
  getPositionCloseErrorMessage,
} from '../../utils/positionClose';

// Reuse the same modals the Dashboard uses
import UpdateStopLossModal from '../UpdateStopLossModal';
import PartialCloseModal from '../PartialCloseModal';
import UpdateTakeProfitModal from '../UpdateTakeProfitModal';
import ConfirmModal from '../ui/ConfirmModal';

const OpenPositionsWidget = ({ hideTitle = false }) => {
  // Data + raw actions from the provider
  const {
    openPositions,
    loadingTradeId,
    connectedAccountId,
  } = useAccountData();
  const { tradeContext } = useTrade();
  const activeAccountId = connectedAccountId || tradeContext?.accountId || null;

  // --- Modal/confirm orchestration (copied from Dashboard pattern) ---
  const [isUpdateSlModalOpen, setIsUpdateSlModalOpen] = useState(false);
  const [selectedTradeForSlUpdate, setSelectedTradeForSlUpdate] = useState(null);

  const [isPartialCloseModalOpen, setIsPartialCloseModalOpen] = useState(false);
  const [selectedTradeForPartialClose, setSelectedTradeForPartialClose] = useState(null);

  const [isUpdateTpModalOpen, setIsUpdateTpModalOpen] = useState(false);
  const [selectedTradeForTpUpdate, setSelectedTradeForTpUpdate] = useState(null);

  // Confirm modal state
  const [confirmTradeId, setConfirmTradeId] = useState(null);
  const [confirmOrphanPayload, setConfirmOrphanPayload] = useState(null);
  const [confirmCloseAllPositions, setConfirmCloseAllPositions] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleCloseTrade = (tradeId, opts) => {
    if (!tradeId && opts?.orphan) {
      setConfirmOrphanPayload({
        account_id: activeAccountId,
        ticket: opts.ticket,
        symbol: opts.symbol,
        volume: opts.volume,
        direction: opts.direction,
      });
      setConfirmTradeId(`orphan:${opts.ticket}`);
      return;
    }
    setConfirmTradeId(tradeId);
    setConfirmOrphanPayload(null);
  };

  const executeCloseTrade = async () => {
    if (!confirmTradeId) return;
    setConfirmLoading(true);
    try {
      if (confirmOrphanPayload) {
        if (!confirmOrphanPayload.account_id) {
          throw new Error('No active account selected.');
        }
        await closeOrphanPosition(confirmOrphanPayload);
        toast.success('Broker position close submitted');
      } else {
        await closeOpenPosition(null, {
          closeTradeApi,
          closeOrphanPositionApi: closeOrphanPosition,
          fallbackAccountId: activeAccountId,
          tradeId: confirmTradeId,
        });
        toast.success('Trade closed successfully');
      }
    } catch (err) {
      toast.error(`Failed to close trade. ${getPositionCloseErrorMessage(err)}`);
    } finally {
      setConfirmLoading(false);
      setConfirmTradeId(null);
      setConfirmOrphanPayload(null);
    }
  };

  const handleCloseAllPositions = (positions) => {
    const list = Array.isArray(positions) ? positions : [];
    if (list.length === 0) return;
    setConfirmCloseAllPositions(list);
  };

  const executeCloseAllPositions = async () => {
    const positions = Array.isArray(confirmCloseAllPositions) ? confirmCloseAllPositions : [];
    if (positions.length === 0) return;

    setConfirmLoading(true);
    try {
      const result = await closeOpenPositionsSequentially(positions, {
        closeTradeApi,
        closeOrphanPositionApi: closeOrphanPosition,
        fallbackAccountId: activeAccountId,
      });

      if (result.failures.length > 0) {
        const firstError = getPositionCloseErrorMessage(result.failures[0].error);
        toast.error(`Closed ${result.successes.length}/${result.total} positions. ${result.failures.length} failed. ${firstError}`);
      } else {
        toast.success(`Close submitted for ${result.successes.length} positions`);
      }
    } finally {
      setConfirmLoading(false);
      setConfirmCloseAllPositions(null);
    }
  };

  // Open/close modals (same names/shape as Dashboard)
  const handleOpenUpdateSlModal = (trade) => {
    setSelectedTradeForSlUpdate(trade);
    setIsUpdateSlModalOpen(true);
  };
  const handleCloseUpdateSlModal = () => {
    setIsUpdateSlModalOpen(false);
    setSelectedTradeForSlUpdate(null);
  };

  const handleOpenPartialCloseModal = (trade) => {
    setSelectedTradeForPartialClose(trade);
    setIsPartialCloseModalOpen(true);
  };
  const handleClosePartialCloseModal = () => {
    setIsPartialCloseModalOpen(false);
    setSelectedTradeForPartialClose(null);
  };

  const handleOpenUpdateTpModal = (trade) => {
    setSelectedTradeForTpUpdate(trade);
    setIsUpdateTpModalOpen(true);
  };
  const handleCloseUpdateTpModal = () => {
    setIsUpdateTpModalOpen(false);
    setSelectedTradeForTpUpdate(null);
  };

  // Generic success handler to close whichever modal is open
  const handleTradeActionSuccess = () => {
    if (isUpdateSlModalOpen) handleCloseUpdateSlModal();
    if (isPartialCloseModalOpen) handleClosePartialCloseModal();
    if (isUpdateTpModalOpen) handleCloseUpdateTpModal();
    // Provider should push fresh positions via WS
  };

  return (
    <Widget title="Open Positions" className="w-full" hideTitle={hideTitle}>
      {/* Pass the orchestrated handlers into the table component */}
      <OpenPositions
        bare
        openPositions={openPositions}
        closeTrade={handleCloseTrade}
        closeAllPositions={handleCloseAllPositions}
        updateStopLoss={handleOpenUpdateSlModal}
        updateTakeProfit={handleOpenUpdateTpModal}
        partialClose={handleOpenPartialCloseModal}
        loadingTradeId={loadingTradeId}
        bulkCloseLoading={confirmLoading && !!confirmCloseAllPositions}
        fallbackAccountId={activeAccountId}
      />

      {/* Mount the same modals Dashboard uses so actions actually have a UI */}
      {selectedTradeForSlUpdate && (
        <UpdateStopLossModal
          isOpen={isUpdateSlModalOpen}
          onClose={handleCloseUpdateSlModal}
          trade={selectedTradeForSlUpdate}
          onSuccessfulUpdate={handleTradeActionSuccess}
        />
      )}

      {selectedTradeForPartialClose && (
        <PartialCloseModal
          isOpen={isPartialCloseModalOpen}
          onClose={handleClosePartialCloseModal}
          trade={selectedTradeForPartialClose}
          onSuccessfulPartialClose={handleTradeActionSuccess}
        />
      )}

      {selectedTradeForTpUpdate && (
        <UpdateTakeProfitModal
          isOpen={isUpdateTpModalOpen}
          onClose={handleCloseUpdateTpModal}
          trade={selectedTradeForTpUpdate}
          onSuccessfulTpUpdate={handleTradeActionSuccess}
        />
      )}

      {/* Close trade confirmation modal */}
      <ConfirmModal
        open={!!confirmTradeId}
        onClose={() => !confirmLoading && (setConfirmTradeId(null), setConfirmOrphanPayload(null))}
        onConfirm={executeCloseTrade}
        loading={confirmLoading}
        title={confirmOrphanPayload ? 'Close Broker Position' : 'Close Trade'}
        message={confirmOrphanPayload
          ? `This broker position (${confirmOrphanPayload.symbol} ${confirmOrphanPayload.direction} ${confirmOrphanPayload.volume}) is not linked to a Valpha trade record. Submit a market close order to the broker?`
          : 'Are you sure you want to close this trade? This action cannot be undone.'}
        confirmLabel={confirmOrphanPayload ? 'Close Position' : 'Close Trade'}
        confirmVariant="danger"
      />

      <ConfirmModal
        open={!!confirmCloseAllPositions}
        onClose={() => !confirmLoading && setConfirmCloseAllPositions(null)}
        onConfirm={executeCloseAllPositions}
        loading={confirmLoading}
        title="Close All Positions"
        message={`Close all ${confirmCloseAllPositions?.length || 0} visible open positions? This submits market close orders one by one.`}
        confirmLabel="Close All"
        confirmVariant="danger"
      />
    </Widget>
  );
};

export default OpenPositionsWidget;
